import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '../api/client.js';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder.js';
import clsx from 'clsx';

/**
 * Reusable voice-input (dictation) button.
 *
 * The system-wide pattern: any new button/field that takes user input should
 * offer a voice option. Drop this next to a text input or textarea — the BDM
 * taps the mic, speaks, and the transcript is handed back via onTranscript so
 * the caller can fill or append the field. Recording stops on a second tap and
 * is transcribed automatically (Whisper, via /notes/transcribe).
 *
 * Props:
 *   onTranscript(text) — called with the transcribed text
 *   title — accessible label / tooltip (default "Dictate")
 *   className — extra classes (e.g. positioning)
 */
export default function VoiceInputButton({ onTranscript, title = 'Dictate', className }) {
  const rec = useVoiceRecorder();

  const transcribe = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('audio', rec.blob, `dictation-${Date.now()}.webm`);
      const r = await api.post('/notes/transcribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data.data.text;
    },
    onSuccess: (text) => {
      if (text && text.trim()) onTranscript?.(text.trim());
      rec.reset();
    },
  });

  // Once a clip is captured, transcribe it immediately.
  useEffect(() => {
    if (rec.blob && !transcribe.isPending) transcribe.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.blob]);

  const tooltip = rec.error || (transcribe.isError ? 'Transcription failed — tap to retry' : title);

  return (
    <button
      type="button"
      title={tooltip}
      aria-label={title}
      onClick={rec.recording ? rec.stop : rec.start}
      disabled={transcribe.isPending}
      className={clsx(
        'shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-sm transition',
        rec.recording ? 'bg-crimson text-white animate-pulse'
          : transcribe.isPending ? 'bg-panel text-muted cursor-wait'
          : 'bg-panel text-navy hover:bg-navy hover:text-white',
        className
      )}>
      {transcribe.isPending ? '…' : rec.recording ? '■' : '🎙'}
    </button>
  );
}
